import type { TranslationKey } from '@/i18n/dictionary'
import type { CompanyModules, ModuleName } from '@/types/api'

/**
 * Which parts of the application a customer has switched on.
 *
 * The owner can turn a module off for one company — 5S only, say, for a plant
 * that does not run the improvement scheme. The server is what actually
 * refuses (`module:{name}` middleware, 403 with the module named in the body);
 * this file is what lets the screens say so first, by hiding the navigation and
 * putting a sentence where the page would have been.
 *
 * Data is never deleted when a module goes off, and turning it back on brings
 * everything back. That is why the empty state below reads as "not switched on
 * for you" rather than as an error: nothing is broken and nothing was lost.
 */

export const MODULES: readonly ModuleName[] = ['submissions', 'fiveS', 'analytics', 'mes']

/**
 * Each module's name, as both sides print it.
 *
 * One set of keys for the owner's switches and the customer's empty state, so
 * the module the owner switched off and the one the worker is told is off are
 * called the same thing — which is the whole conversation when the customer
 * rings to ask where it went.
 */
export const MODULE_LABEL: Record<ModuleName, TranslationKey> = {
  submissions: 'module.submissions',
  fiveS: 'module.fiveS',
  analytics: 'module.analytics',
  mes: 'module.mes',
}

/**
 * Whether a module is on.
 *
 * Absent means on, and that default is load-bearing. A session restored from
 * an API that predates modules carries no `modules` at all, and so does every
 * company nobody has ever touched — reading either as "everything off" would
 * hide the whole application from every customer on the day this ships.
 */
export function moduleEnabled(
  modules: Partial<CompanyModules> | null | undefined,
  name: ModuleName,
): boolean {
  return modules?.[name] ?? true
}

export function isModuleName(value: unknown): value is ModuleName {
  return typeof value === 'string' && (MODULES as readonly string[]).includes(value)
}

/*
 * The customer routes each module owns.
 *
 * Paths, not route definitions, because two different things need the answer:
 * the route guard deciding what to render, and the 403 listener deciding
 * whether the refusal it just heard is about the page on screen or about a
 * widget on it. Home, for one, reads submissions and must not be replaced
 * wholesale by "not switched on" because one tile was refused.
 *
 * `/admin/obrasci` is the 5S checklists — the server puts the admin template
 * routes behind `module:fiveS` too, so the tab goes with the module.
 */
const ROUTES: [prefix: string, module: ModuleName][] = [
  ['/prijave', 'submissions'],
  ['/zadaci', 'submissions'],
  ['/5s', 'fiveS'],
  ['/analitika', 'analytics'],
  ['/admin/obrasci', 'fiveS'],
  ['/admin/integracija', 'mes'],
]

/**
 * The module a path belongs to, or null for a page every company has.
 *
 * Matched on whole segments — `/5s` and `/5s/nova`, never `/5sx` — so a later
 * route that happens to share a prefix does not inherit a guard it was never
 * meant to have.
 */
export function moduleForPath(pathname: string): ModuleName | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

  for (const [prefix, module] of ROUTES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return module
  }

  return null
}

/**
 * Fired on `window` when the API refuses a request because a module is off.
 *
 * The axios interceptor has no React around it, so it announces the refusal
 * and the shell decides what to do: re-read the session, so the navigation
 * catches up with a change the owner made while this person was signed in, and
 * replace the page with the empty state if the refusal was about that page.
 */
export const MODULE_DISABLED_EVENT = 'kapro:module-disabled'

export interface ModuleDisabledDetail {
  module: ModuleName
}
